import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BrandLogo, BrandMonogram } from "@/components/BrandLogo";

/**
 * LegalPageLayout — thin frame shared by /privacy and /terms.
 *
 * Same navbar + footer as LandingPage so brand continuity holds, and
 * the content column uses a long-form prose container tuned for
 * readability at 720px max width. Public route — no auth guards.
 */
function LegalPageLayout({ title, effectiveDate, testId, children }) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · MatchBroker`;
    return () => {
      document.title = prev;
    };
  }, [title]);

  return (
    <div className="mb-page min-h-screen" data-testid={testId}>
      {/* Header — mirrors LandingPage exactly */}
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-5 sm:px-6 sm:py-6">
        <Link
          to="/"
          className="mb-focus-ring inline-flex flex-shrink-0 items-center rounded-lg"
          data-testid="legal-header-logo"
        >
          <BrandLogo className="h-9" wordmarkClassName="hidden sm:inline" />
        </Link>
        <nav className="flex flex-shrink-0 items-center gap-3 sm:gap-6">
          <Link
            to="/login"
            className="mb-transition whitespace-nowrap text-sm font-medium text-gray-600 hover:text-gray-900"
            data-testid="legal-header-login"
          >
            Log in
          </Link>
          <Link to="/register" data-testid="legal-header-cta">
            <span className="mb-focus-ring inline-flex h-10 items-center whitespace-nowrap rounded-xl bg-[#1B2B4B] px-4 text-sm font-semibold text-white hover:bg-[#14213A] sm:px-5">
              Apply for Access
            </span>
          </Link>
        </nav>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-3xl px-5 pt-10 pb-16 sm:px-6 sm:pt-16 sm:pb-24">
        <div className="mb-10">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-navy/15 bg-brand-navy-soft px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-navy sm:text-[11px] sm:tracking-[0.18em]">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            Legal
          </div>
          <h1
            className="text-[2rem] font-bold leading-[1.15] tracking-tight text-gray-900 sm:text-5xl"
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          <p
            className="mt-3 text-sm text-gray-500"
            data-testid="legal-effective-date"
          >
            Effective {effectiveDate}
          </p>
        </div>

        <article className="prose prose-gray max-w-none [&_h2]:mt-12 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-gray-900 sm:[&_h2]:text-2xl [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_p]:leading-relaxed [&_p]:text-gray-600 [&_p]:text-[15px] sm:[&_p]:text-base [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-gray-600 [&_ul]:text-[15px] sm:[&_ul]:text-base [&_li]:mt-1.5 [&_li]:leading-relaxed [&_a]:text-brand-navy [&_a]:font-medium hover:[&_a]:underline">
          {children}
        </article>

        <div className="mt-14 rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
            Contact
          </div>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            Questions about this document?{" "}
            <a
              href="mailto:info@matchbroker.ph"
              className="font-medium text-brand-navy hover:underline"
              data-testid="legal-contact-email"
            >
              info@matchbroker.ph
            </a>{" "}
            — MatchBroker is a service of Vancor Distribution, OPC
            (Metro Manila, Philippines).
          </p>
        </div>
      </main>

      {/* Footer — mirrors LandingPage with legal links surfaced */}
      <footer className="border-t border-gray-100">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-10 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2.5">
            <BrandMonogram className="h-6" />
            <span className="text-sm font-bold tracking-tight leading-none">
              <span className="text-brand-green">Match</span>
              <span className="text-brand-navy">Broker</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-500">
            <Link
              to="/privacy"
              className="hover:text-gray-900"
              data-testid="legal-footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-gray-900"
              data-testid="legal-footer-terms"
            >
              Terms of Service
            </Link>
            <span className="text-gray-400">
              © {new Date().getFullYear()} MatchBroker
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LegalPageLayout;
